// Nag decision plus the state-sync subscription the watchdog uses to see it.
// Decision stays pure so the truth table is unit-testable without mounting ProxyWatchdog.
//
// Per rule: think-in-systems (ai.knowledge/global) — decision separate from UI.

import { StatusUpdateState, type State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Proxy } from '#~/agent/types.js';
import { isVersionSnoozed, type DesktopUpdateSnooze } from '#~/electron/update-snooze.js';
import { useMultiProxyStateQuery } from '#~/statequery/hook.js';

export const NAG_STATES = [
  StatusUpdateState.UPDATE_STATE_UPDATE_AVAILABLE,
  StatusUpdateState.UPDATE_STATE_DOWNLOADING,
  StatusUpdateState.UPDATE_STATE_WAITING_TO_INSTALL
];

export interface NagInput {
  state: StatusUpdateState | undefined;
  latestVersion: string | undefined;
  snooze: DesktopUpdateSnooze | null;
  /** False until the renderer has learned the snooze from main (or Later). */
  snoozeKnown: boolean;
  now: number;
  /** Version the toast was last posted for in this renderer (module-level, not React state). */
  lastPostedVersion: string | null;
  /** notifications.has('update-available') */
  alreadyPosted: boolean;
}

export function shouldPostUpdateNag(input: NagInput): boolean {
  if (!input.snoozeKnown) return false;
  if (!NAG_STATES.includes(input.state ?? StatusUpdateState.UPDATE_STATE_UNKNOWN)) return false;
  if (!input.latestVersion) return false;
  if (isVersionSnoozed(input.latestVersion, input.snooze, input.now)) return false;
  if (input.lastPostedVersion === input.latestVersion && input.alreadyPosted) return false;
  return true;
}

// Module-level so the query identity is stable across renders. A selector
// allocated inside the component re-keys the state-sync subscription on every
// render, which tears the subscription down and rebuilds it instead of
// sampling — the watchdog then misses the very updates it exists to observe.
const selectUpdateStatus = (state: T_Maybe<State>) => ({
  state: state?.status?.update?.state,
  latestVersion: state?.status?.update?.latestVersion
});

// The watchdog must observe state-sync updates, not incidental renders.
export function useProxyUpdateStatus(proxyId: Proxy['id']): {
  state: StatusUpdateState | undefined;
  latestVersion: string | undefined;
} {
  const map = useMultiProxyStateQuery<{
    state: StatusUpdateState | undefined;
    latestVersion: string | undefined;
  }>(selectUpdateStatus);
  return map[proxyId] ?? { state: undefined, latestVersion: undefined };
}
